import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDepostionReport } from "reduxStore/action/webReportActions";
// import { getReportPropertyList } from "reduxStore/action/reportActions";
// import TableDataNotFound from "component/TableDataNotFound";
import strings from "translate/language";
import { handleDetailResponse, handleListResponse } from "utils/utils";
import { admin_id } from "utils/constants";
import {
  formatPhoneForRole,
  shouldMaskPhoneForUser,
} from "utils/phoneMask";
import Breadcrumb from "component/common/breadcrumb";
import TableListSkeleton from "component/common/TableListSkeleton";
import Select from "react-select";
import ExcelJS from "exceljs";
import propertyService from "reduxStore/service/propertyService";
import { errorToast } from "utils/httpClient";
import Pagination from "component/common/Pagination";
import ClearableField from "component/common/ClearableField";
import { getMasterList } from "reduxStore/action/masterActions";
import StatusBadge from "component/common/StatusBadge";
import { getClosingStatusBadgeClass } from "utils/appointmentListStatusDisplay";
import {
  formatIstDateTime,
  LATEST_LEAD_STATUS_FILTER_OPTIONS,
} from "utils/latestLeadStatusFilter";
import "../Property/PropertyListing.css";
import "./DepositionReport.css";
// import usePage from "utils/usePage";

const DepositionReport = ({ isAuth = false }) => {
  const dispatch = useDispatch();
  const permissionSelector = useSelector((state) => state.permission);
  const loginSelector = useSelector((state) => state.login);
  const { isAdmin = false } = permissionSelector;
  const roleId = handleDetailResponse(loginSelector?.response?.data)?.role_id;
  const showAllProperties =
    isAdmin || String(roleId || "") === String(admin_id || "");
  const shouldMaskPhone = shouldMaskPhoneForUser({
    permissionResponse: permissionSelector?.response,
    isAdmin,
    roleId,
  });
  const displayPhone = (value) => formatPhoneForRole(value, shouldMaskPhone);
  const [formVaild, setFormVaild] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(true);
  const todayDate = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    property_title: "",
    start_date: todayDate,
    end_date: todayDate,
    client_name: "",
    phone_number: "",
    sourcing_manager: "",
    closing_manager: "",
    lead_source: "",
    visit_type: "",
    latest_lead_status: "",
  });
  const [expandedCell, setExpandedCell] = useState(null); // { rowIndex, colKey, text }
  const [statusHistoryModal, setStatusHistoryModal] = useState(null);

  const [propertyList, setPropertyList] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  const visitTypeOptions = [
    { value: "Fresh", label: strings.freshVisit || "Fresh" },
    { value: "Revisit", label: strings.revisit || "Revisit" },
  ];

  const masterDataSelector = useSelector((state) => state?.masterData);

  const MAX_CELL_CHARS = 30;
  /** When false, date columns stay visible but values are not shown (UI + Excel). */
  const SHOW_FOLLOWUP_DATE_VALUES = false;
  const SHOW_REVISIT_DATE_VALUES = false;
  const REMARK_CHAIN_SEP = " -> ";

  const isCmRemarkPart = (part) => {
    const p = String(part || "").trim();
    return (
      p.startsWith("(CM)") ||
      /^re-allocated visitor to new closing manager/i.test(p) ||
      /^re-allocated from\b/i.test(p) ||
      /^re-allocated to\b/i.test(p) ||
      /^assign appointment to new cm/i.test(p) ||
      p.startsWith("[CM Assignment]")
    );
  };

  const parseRemarkChain = (text) => {
    const raw = text && String(text).trim() ? String(text).trim() : "";
    if (!raw || raw === "N/A") return [];
    if (!raw.includes(REMARK_CHAIN_SEP)) return [raw];
    return raw
      .split(REMARK_CHAIN_SEP)
      .map((part) => part.trim())
      .filter(Boolean);
  };

  const renderRemarkPart = (part) =>
    isCmRemarkPart(part) ? <strong>{part}</strong> : part;

  const toLatestFirstRemarkParts = (text) => {
    const parts = parseRemarkChain(text);
    if (!parts.length) return [];
    return [...parts].reverse();
  };

  /** Preview / table cell: as captured (oldest → newest). */
  const formatRemarkChainPreviewText = (text) => {
    const parts = parseRemarkChain(text);
    if (!parts.length) return "N/A";
    return parts.join(REMARK_CHAIN_SEP);
  };

  const renderRemarkChainPreview = (text) => {
    const parts = parseRemarkChain(text);
    if (!parts.length) return "N/A";
    if (parts.length === 1) return renderRemarkPart(parts[0]);

    return parts.map((part, index) => (
      <React.Fragment key={`remark-part-${index}`}>
        {index > 0 ? (
          <span className="deposition-remark-chain__inline-sep">
            {REMARK_CHAIN_SEP}
          </span>
        ) : null}
        {renderRemarkPart(part)}
      </React.Fragment>
    ));
  };

  /** Modal timeline: latest on top, Latest / Previous badges. */
  const renderRemarkChainTimeline = (text) => {
    const parts = toLatestFirstRemarkParts(text);
    if (!parts.length) return "N/A";
    if (parts.length === 1) {
      return (
        <p className="deposition-remark-chain__single">
          {renderRemarkPart(parts[0])}
        </p>
      );
    }

    const total = parts.length;
    return (
      <ol className="deposition-remark-chain">
        {parts.map((part, index) => {
          const isLatest = index === 0;
          const stepNumber = total - index;
          return (
            <li
              key={`remark-step-${stepNumber}`}
              className={`deposition-remark-chain__item${
                isLatest ? " deposition-remark-chain__item--latest" : ""
              }`}
            >
              <span className="deposition-remark-chain__step" aria-hidden="true">
                {stepNumber}
              </span>
              <div className="deposition-remark-chain__body">
                <div className="deposition-remark-chain__content">
                  <p className="deposition-remark-chain__text">
                    {renderRemarkPart(part)}
                  </p>
                  <span
                    className={`deposition-remark-chain__badge${
                      isLatest
                        ? " deposition-remark-chain__badge--latest"
                        : " deposition-remark-chain__badge--previous"
                    }`}
                  >
                    {isLatest ? "Latest" : "Previous"}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    );
  };

  const renderExpandableCell = (rowIndex, colKey, value) => {
    const text = value && String(value).trim() ? String(value) : "N/A";
    const isRemarkChain = colKey === "last_followup_remark";
    const previewText = isRemarkChain
      ? formatRemarkChainPreviewText(text)
      : text;
    const isLong = previewText.length > MAX_CELL_CHARS;

    if (!isLong) {
      return (
        <span
          style={{ display: "block", maxHeight: "2.5em", overflow: "hidden" }}
        >
          {isRemarkChain ? renderRemarkChainPreview(text) : text}
        </span>
      );
    }

    return (
      <span
        style={{ display: "block", maxHeight: "2.5em", overflow: "hidden" }}
      >
        {`${previewText.slice(0, MAX_CELL_CHARS)}...`}{" "}
        <button
          type="button"
          className="deposition-expand-trigger"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedCell({ rowIndex, colKey, text });
          }}
        >
          See more
        </button>
      </span>
    );
  };
  const [sortOrder, setSortOrder] = useState("oldest");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state
  const [apiError, setApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(0); // Current page number (0-indexed)
  const [totalRecords, setTotalRecords] = useState(0); // Total records from API
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
  const tableRegionRef = useRef(null);

  // Trackpads often emit separate X-only / Y-only wheel events in one gesture.
  // Lock the dominant axis for the whole gesture so the table cannot move diagonally.
  useEffect(() => {
    const el = tableRegionRef.current;
    if (!el) return undefined;

    let axisLock = null;
    let lastEventAt = 0;
    const GESTURE_IDLE_MS = 160;

    const onWheel = (event) => {
      let dx = event.deltaX;
      let dy = event.deltaY;

      if (event.shiftKey && Math.abs(dx) < 0.01 && Math.abs(dy) >= 0.01) {
        dx = dy;
        dy = 0;
      }

      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (absX < 0.01 && absY < 0.01) return;

      const now = performance.now();
      if (!axisLock || now - lastEventAt > GESTURE_IDLE_MS) {
        axisLock = absY >= absX ? "y" : "x";
      }
      lastEventAt = now;

      event.preventDefault();
      event.stopPropagation();

      if (axisLock === "y") {
        if (absY >= 0.01) el.scrollTop += dy;
      } else if (absX >= 0.01) {
        el.scrollLeft += dx;
      }
    };

    el.addEventListener("wheel", onWheel, { capture: true, passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel, { capture: true });
    };
  }, [loading, reportData, apiError]);

  useEffect(() => {
    if (!statusHistoryModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setStatusHistoryModal(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [statusHistoryModal]);

  const extractReportRows = (payload) => {
    // Backend already dedupes disposition rows — avoid a second full-array pass here.
    if (!payload) return { rows: [], total: 0 };
    if (Array.isArray(payload)) {
      return { rows: payload, total: payload.length };
    }
    if (Array.isArray(payload.data)) {
      return {
        rows: payload.data,
        total: payload.total ?? payload.data.length,
      };
    }
    if (payload.data?.records || payload.data?.data) {
      const records = payload.data.records || payload.data.data || [];
      return {
        rows: Array.isArray(records) ? records : [],
        total:
          payload.data.total ??
          payload.data.totalCount ??
          (Array.isArray(records) ? records.length : 0),
      };
    }
    return { rows: [], total: 0 };
  };

  const applyDepositionPayload = useCallback((payload) => {
    const status = Array.isArray(payload) ? 200 : Number(payload?.status);
    if (status === 200) {
      const { rows, total } = extractReportRows(payload);
      setReportData(rows);
      setTotalRecords(total);
      setCurrentPage(0);
      setLoading(false);
      setApiError(false);
      setErrorMessage("");
      return;
    }

    setReportData([]);
    setTotalRecords(0);
    setLoading(false);
    setApiError(true);
    setErrorMessage(
      (payload && !Array.isArray(payload) && payload.message) ||
        "Failed to fetch data. Please try again.",
    );
  }, []);

  const loadDepositionReport = useCallback(
    async (params) => {
      setLoading(true);
      setApiError(false);
      setErrorMessage("");
      try {
        const payload = await dispatch(getDepostionReport(params));
        applyDepositionPayload(payload);
      } catch {
        setReportData([]);
        setTotalRecords(0);
        setLoading(false);
        setApiError(true);
        setErrorMessage("Failed to fetch data. Please try again.");
      }
    },
    [applyDepositionPayload, dispatch],
  );

  // API returns oldest→newest. Avoid O(n log n) Date parsing on every fetch.
  const sortedReportData = useMemo(() => {
    if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
      return [];
    }
    if (sortOrder === "oldest") return reportData;
    return reportData.slice().reverse();
  }, [reportData, sortOrder]);

  // Backend currently returns the full result set (limit/offset not applied server-side).
  // Slice here so the table only renders the current page; Pagination uses totalRecords.
  const pagedTableRows = useMemo(() => {
    if (!sortedReportData.length) return [];
    const start = currentPage * itemsPerPage;
    return sortedReportData.slice(start, start + itemsPerPage);
  }, [sortedReportData, currentPage, itemsPerPage]);

  const columnsForExcel = [
    { header: "Sr.No.", key: "sr_no", width: 10 },
    { header: "Region", key: "region", width: 15 },
    { header: "Visit Type", key: "revisitType", width: 15 },
    { header: "Date", key: "created_date", width: 15 },
    { header: "Client Name", key: "customer_name", width: 20 }, // moved up
    { header: "Property Title", key: "property_name", width: 40 },
    { header: "Mobile", key: "customer_mobile", width: 20 },
    // { header: "Role Title", key: "role_title", width: 20 },
    { header: "Email", key: "customer_email", width: 30 },
    { header: "Resi. Location", key: "customer_location", width: 20 },
    { header: "Sub Location", key: "customer_sub_location", width: 20 },
    { header: "Profession", key: "customer_designation", width: 20 },
    { header: "Work Location", key: "customer_office_address", width: 20 },
    { header: "Ethnicity", key: "ethnicity", width: 20 },
    { header: "Visiting With", key: "visit_with", width: 20 },
    { header: "Visitor Status", key: "qualified", width: 20 },
    { header: "Lead Priority", key: "lead_priority", width: 20 },
    {
      header: "Possession Period",
      key: "expected_possession_period",
      width: 25,
    },
    { header: "Config", key: "configuration", width: 15 },
    { header: "Budget", key: "budget", width: 15 },
    { header: "Purpose", key: "purpose", width: 20 },
    { header: "Lead Source", key: "lead_source", width: 20 },
    { header: "CP Firm Name", key: "cp_firm_name", width: 50 },
    { header: "CP Name", key: "cp_name", width: 50 },
    { header: "CP Contact", key: "cp_contact", width: 20 },
    { header: "RERA Number", key: "rera_number", width: 20 },
    { header: "Referrer Name", key: "referrer_name", width: 30 },
    { header: "Referrer Contact", key: "referrer_contact", width: 20 },
    { header: "Sourcing Manager", key: "sourcing_manager", width: 30 },
    { header: "Closing Manager", key: "closing_manager", width: 30 },
    { header: "Latest / Last Updated Lead Status", key: "lead_status", width: 28 },
    { header: "Cancellation Date", key: "cancellation_date", width: 28 },
    { header: "Booking Date", key: "booking_date", width: 28 },
    { header: "Registration Date", key: "registration_date", width: 28 },
    { header: "Walkin - Remark", key: "first_appointment_remarks", width: 60 },
    { header: "Followup Date", key: "followup_dates", width: 45 },
    { header: "Follow-up Remark", key: "last_followup_remark", width: 60 },
    // { header: "NFD", key: "second_followup_date", width: 20 },
    // { header: "STATUS", key: "followup_status", width: 20 },
    { header: "Lost Reason", key: "lost_reason", width: 60 },
    { header: "Lost Remark", key: "lost_remark", width: 60 },
    { header: "Revisit Date", key: "revisit_dates", width: 35 },
  ];

  useEffect(() => {
    loadDepositionReport({
      limit: 15,
      offset: 0,
    });
    dispatch(getMasterList({ type: 13 }));
    // Do not use Redux PROPERTY_LIST here: Property Management (and others) call
    // getAllProperty({ limit: 10 }) into the same slice and would shrink this dropdown to 10 rows.
    let cancelled = false;
    const propertyRequest = showAllProperties
      ? propertyService.getAllProperty({
          skip_metrics: true,
          limit: 10000,
          offset: 0,
        })
      : propertyService.getAgentProperty({ offset: 0, limit: 10000 });

    propertyRequest
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.status === 200) {
          let list = handleListResponse(res.data.data);
          if (!showAllProperties) {
            list = list.filter((item) => item.status === true);
          }
          setPropertyList(list);
        } else {
          setPropertyList([]);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const { response = strings.initialError } = e;
        errorToast(response?.data?.message);
        setPropertyList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, loadDepositionReport, showAllProperties]);

  useEffect(() => {
    if (masterDataSelector?.response?.status === 200) {
      const list = handleListResponse(masterDataSelector.response.data) || [];
      setLeadSourceOptions(
        list
          .map((item) => item?.title)
          .filter(Boolean)
          .map((title) => ({ value: title, label: title })),
      );
    } else {
      setLeadSourceOptions([]);
    }
  }, [masterDataSelector]);

  // If the dataset shrinks, keep the active page in range (e.g. fewer total rows).
  useEffect(() => {
    if (!sortedReportData?.length) return;
    const maxPage = Math.max(
      0,
      Math.ceil(sortedReportData.length / itemsPerPage) - 1,
    );
    setCurrentPage((p) => (p > maxPage ? maxPage : p));
  }, [sortedReportData, itemsPerPage]);

  const handleSelectChange = (selectedOption) => {
    setForm({
      ...form,
      property_title: selectedOption ? selectedOption.value : "",
    });
  };

  const handleLeadSourceSelectChange = (selectedOption) => {
    setForm({
      ...form,
      lead_source: selectedOption ? selectedOption.value : "",
    });
  };

  const handleVisitTypeSelectChange = (selectedOption) => {
    setForm({
      ...form,
      visit_type: selectedOption ? selectedOption.value : "",
    });
  };

  const handleLatestLeadStatusSelectChange = (selectedOption) => {
    setForm({
      ...form,
      latest_lead_status: selectedOption ? selectedOption.value : "",
    });
  };

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;

    if (!form.start_date && form.end_date) {
      formError = true;
      err.start_date = strings.startEndDateErr;
    }

    setFormVaild(err);
    return formError;
  };

  // Page change: client-side only — full data is already in reportData (API returns all rows).
  // When server-side pagination is added, dispatch here with offset: newPage * itemsPerPage.
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(0);
  };

  const filterDetail = () => {
    if (!handleValid()) {
      setCurrentPage(0);
      loadDepositionReport({
        ...form,
        start_date: form.start_date || todayDate,
        end_date: form.end_date || todayDate,
        limit: itemsPerPage,
        offset: 0,
      });
    }
  };

  const filterReset = () => {
    setForm({
      property_title: "",
      start_date: todayDate,
      end_date: todayDate,
      client_name: "",
      phone_number: "",
      sourcing_manager: "",
      closing_manager: "",
      lead_source: "",
      visit_type: "",
      latest_lead_status: "",
    });
    setCurrentPage(0);
    loadDepositionReport({
      start_date: todayDate,
      end_date: todayDate,
      limit: itemsPerPage,
      offset: 0,
    });
  };

  const handleInputField = (e) => {
    const nextFormState = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(nextFormState);
    setFormVaild({ ...formVaild, [e.target.name]: "" });
  };

  const clearFilterField = (name, emptyValue = "") => {
    setForm((prev) => ({ ...prev, [name]: emptyValue }));
    setFormVaild((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    if (form.start_date === "") {
      setEndDateDisable(true);
    } else {
      setEndDateDisable(false);
    }
  }, [form.start_date]);

  const formatDate = (date) => {
    if (!date) return "N/A";

    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "N/A";

    const day = ("0" + d.getDate()).slice(-2);
    const month = ("0" + (d.getMonth() + 1)).slice(-2);
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "N/A";
    const datePart = formatDate(d);
    const hours = d.getHours();
    const minutes = ("0" + d.getMinutes()).slice(-2);
    const suffix = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    return `${datePart} · ${hour12}:${minutes} ${suffix}`;
  };

  const getFollowupDatesText = (item) => {
    const dates = Array.isArray(item?.followup_dates)
      ? item.followup_dates
      : [item?.first_followup_date, item?.second_followup_date].filter(Boolean);

    const formatted = dates
      .map((d) => formatDate(d))
      .filter((v) => v && v !== "N/A");

    return formatted.length > 0 ? formatted.join(", ") : "N/A";
  };

  const getRevisitDatesText = (item) => {
    const dates = Array.isArray(item?.revisit_dates)
      ? item.revisit_dates
      : item?.revisit_appointment
        ? [item.revisit_appointment]
        : [];

    const formatted = dates
      .map((d) => formatDate(d))
      .filter((v) => v && v !== "N/A");

    return formatted.length > 0 ? formatted.join(", ") : "N/A";
  };

  const capitalizeWords = (text = "") => {
    if (typeof text !== "string") {
      return "";
    }

    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderStateBadge = (label) => (
    <StatusBadge label={label || "N/A"} size="sm" />
  );

  const renderVisitorQualificationBadge = (qualified) => {
    if (qualified === true) {
      return (
        <StatusBadge
          label={strings.qualified}
          badgeClass="visitor-status-badge--qualified"
          size="sm"
        />
      );
    }
    if (qualified === false) {
      return (
        <StatusBadge
          label={strings.unQualified}
          badgeClass="visitor-status-badge--unqualified"
          size="sm"
        />
      );
    }
    return (
      <StatusBadge
        label="N/A"
        badgeClass="visitor-status-badge--default"
        size="sm"
      />
    );
  };

  const renderVisitTypeBadge = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "revisit") {
      return (
        <StatusBadge
          label={value}
          badgeClass="visitor-status-badge--revisit"
          size="sm"
        />
      );
    }
    if (normalized === "fresh") {
      return (
        <StatusBadge
          label={value}
          badgeClass="visitor-status-badge--new"
          size="sm"
        />
      );
    }
    return renderStateBadge(value || "N/A");
  };

  const SNAPSHOT_STATUS_LABELS = {
    1: "Not Visit",
    2: "Revisit",
    3: "Booking",
    4: "Visit Cancel",
    5: "Reschedule",
    6: "Not Interested",
    10: "Follow-up",
  };

  const resolveLatestLeadStatusLabel = (item) => {
    const latest = String(item?.latest_lead_status || "").trim();
    if (latest) return latest;
    return SNAPSHOT_STATUS_LABELS[Number(item?.first_appointment_status)] || "N/A";
  };

  const getStatusHistoryItems = (item) => {
    if (Array.isArray(item?.status_history) && item.status_history.length) {
      return item.status_history;
    }
    const label = resolveLatestLeadStatusLabel(item);
    if (!label || label === "N/A") return [];
    return [
      {
        label,
        date: item.createddate || item.created_date || null,
        source: "disposition",
        remark:
          item.last_followup_remark || item.first_appointment_remarks || "",
      },
    ];
  };

  const getHistoryRemarkText = (entry) => {
    const raw = String(entry?.remark || "").trim();
    if (!raw || raw === "N/A") return "";
    return raw;
  };

  const getRowRemarkParts = (item) => {
    const parts = [];
    const walkin = String(item?.first_appointment_remarks || "").trim();
    if (walkin && walkin !== "N/A") parts.push(walkin);
    const followup = String(item?.last_followup_remark || "").trim();
    if (followup && followup !== "N/A") {
      followup.split(/\s*->\s*/).forEach((part) => {
        const text = part.trim();
        if (text && text !== "N/A") parts.push(text);
      });
    }
    // Not Interested / Cancel: lost remark is its own column — count it for history.
    const lostRemark = String(item?.remarks_status_6 || "").trim();
    if (lostRemark && lostRemark !== "N/A") parts.push(lostRemark);
    return parts;
  };

  /**
   * Badge count = Walk-in Remark (0|1) + Follow-up/Revisit Remark chain parts.
   * Matches the two remark columns, not every status timeline event.
   */
  const countDispositionRemarkEntries = (item) => getRowRemarkParts(item).length;

  /** Modal: this row's status timeline only, newest first. */
  const getLeadHistoryRemarkItems = (item) => {
    const history = getStatusHistoryItems(item).filter((entry) =>
      getHistoryRemarkText(entry),
    );
    return [...history].sort((a, b) => {
      const ta = new Date(a?.date || 0).getTime();
      const tb = new Date(b?.date || 0).getTime();
      if (tb !== ta) return tb - ta;
      return 0;
    });
  };

  const renderHistoryRemark = (entry) => {
    const text = getHistoryRemarkText(entry);
    if (!text) {
      return <span className="justo-disposition-history__remark-empty">—</span>;
    }
    return renderRemarkChainPreview(text);
  };

  const renderLeadStatusBadge = (statusOrLabel) => {
    if (
      typeof statusOrLabel === "string" &&
      statusOrLabel &&
      !/^\d+$/.test(statusOrLabel)
    ) {
      return (
        <StatusBadge
          label={statusOrLabel}
          badgeClass={getClosingStatusBadgeClass(statusOrLabel)}
          size="sm"
        />
      );
    }
    const label =
      SNAPSHOT_STATUS_LABELS[Number(statusOrLabel)] ||
      (statusOrLabel != null ? String(statusOrLabel) : "N/A");
    return (
      <StatusBadge
        label={label}
        badgeClass={getClosingStatusBadgeClass(label)}
        size="sm"
      />
    );
  };

  const exportToExcel = async () => {
    // Note: This will export only the current page data (10 records)
    // To export all data, you would need to call API with limit=totalRecords, offset=0
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Disposition Report");

    if (!form.property_title) {
      // worksheet.columns = [
      //   { header: "Sr.No.", key: "sr_no", width: 10 },
      //   { header: "Region", key: "region", width: 15 },
      //   { header: "Visit Type", key: "revisitType", width: 15 },
      //   { header: "Date", key: "created_date", width: 15 },
      //   { header: "Client Name", key: "customer_name", width: 20 },
      //   { header: "Property Title", key: "property_name", width: 40 },
      //   { header: "Mobile", key: "customer_mobile", width: 20 },
      //   // { header: "Role Title", key: "role_title", width: 20 },
      //   { header: "Email", key: "customer_email", width: 30 },
      //   { header: "Resi. Location", key: "customer_location", width: 20 },
      //   { header: "Profession", key: "customer_designation", width: 20 },
      //   { header: "Work Location", key: "customer_office_address", width: 20 },
      //   { header: "Ethnicity", key: "ethnicity", width: 20 },
      //   { header: "Visiting With", key: "visit_with", width: 20 },
      //   { header: "Visitor Status", key: "qualified", width: 20 },
      //   { header: "Lead Priority", key: "lead_priority", width: 20 },
      //   {
      //     header: "Possession Period",
      //     key: "expected_possession_period",
      //     width: 25,
      //   },
      //   { header: "Config", key: "configuration", width: 15 },
      //   { header: "Budget", key: "budget", width: 15 },
      //   { header: "Purpose", key: "purpose", width: 20 },
      //   { header: "Lead Source", key: "lead_source", width: 20 },
      //   { header: "CP Firm Name", key: "cp_firm_name", width: 50 },
      //   { header: "CP Name", key: "cp_name", width: 50 },
      //   { header: "CP Contact", key: "cp_contact", width: 20 },
      //   { header: "RERA Number", key: "rera_number", width: 20 },
      //   { header: "Referrer Name", key: "referrer_name", width: 30 },
      //   { header: "Referrer Contact", key: "referrer_contact", width: 20 },
      //   { header: "Sourcing Manager", key: "sourcing_manager", width: 30 },
      //   { header: "Closing Manager", key: "closing_manager", width: 30 },
      //   { header: "Lead Status", key: "lead_status", width: 20 },
      //   {
      //     header: "Walkin - Remark",
      //     key: "first_appointment_remarks",
      //     width: 60,
      //   },
      //   { header: "1st Follow-Up Date", key: "first_followup_date", width: 30 },
      //   { header: "Follow-up Remark", key: "last_followup_remark", width: 60 },
      //   // { header: "NFD", key: "second_followup_date", width: 20 },
      //   // { header: 'STATUS', key: 'followup_status', width: 20 },
      //   { header: "Lost Reason", key: "lost_reason", width: 60 },
      //   { header: "Lost Remark", key: "lost_remark", width: 60 },
      //   { header: "Revisit Date", key: "revisit_appointment", width: 20 },
      // ];
      worksheet.columns = columnsForExcel;

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = {
          bold: true,
          size: 14,
          color: { argb: "FFFFFF" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4F81BD" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
      });

      sortedReportData.forEach((item, index) => {
        const createdDate = item.createddate
          ? formatDate(item.createddate)
          : "N/A";
        worksheet.addRow({
          sr_no: index + 1,
          revisitType: item.revisitType,
          region: item.region || "N/A",
          created_date: createdDate,
          customer_name: item.customer_name || "N/A",
          property_name: item.property_name || "N/A",
          customer_mobile: displayPhone(item.customer_mobile),
          customer_email: item.customer_email || "N/A",
          // role_title: item.role_title || "N/A",
          customer_location: item.customer_location || "N/A",
          customer_designation: item.customer_designation || "N/A",
          customer_office_address: item.customer_office_address || "N/A",
          ethnicity: item.ethnicity || "N/A",
          visit_with: capitalizeWords(item.visit_with) || "N/A",
          qualified:
            item.qualified === true
              ? "Qualified"
              : item.qualified === false
                ? "Unqualified"
                : "N/A",
          lead_priority: capitalizeWords(item.lead_priority) || "N/A",
          expected_possession_period: item.expected_possession_period
            ? item.expected_possession_period
            : item.expected_possession_date
              ? formatDate(item.expected_possession_date)
              : "N/A",
          configuration: item.configuration || "N/A",
          budget: item.max_budget || "N/A",
          purpose: item.purpose || "N/A",
          lead_source:
            item.referrel_partner === 1
              ? "Referral Partner"
              : item.lead_source_title || "N/A",
          cp_firm_name: item.cp_firm_name || "N/A",
          cp_name: item.cp_name || "N/A",
          cp_contact: item.cp_contact || "N/A",
          rera_number: item.rera_certificate_no || "N/A",
          referrer_name: item.referrer_name || "N/A",
          referrer_contact: item.referrer_contact || "N/A",
          sourcing_manager: item.sourcing_manager || "N/A",
          closing_manager: item.closing_manager || "N/A",
          lead_status: resolveLatestLeadStatusLabel(item) || "N/A",
          cancellation_date: formatIstDateTime(item.cancellation_date),
          booking_date: formatIstDateTime(item.booking_date),
          registration_date: formatIstDateTime(item.registration_date),
          first_appointment_remarks: item.first_appointment_remarks || "N/A",
          followup_dates: SHOW_FOLLOWUP_DATE_VALUES
            ? getFollowupDatesText(item)
            : "N/A",
          last_followup_remark: item.last_followup_remark || "N/A",
          // second_followup_date: secondFollowupDate,
          // followup_status: item.followup_status || 'N/A',
          lost_reason: item.lost_reason_status_6 || "N/A",
          lost_remark: item.remarks_status_6 || "N/A",
          revisit_dates: SHOW_REVISIT_DATE_VALUES
            ? getRevisitDatesText(item)
            : "N/A",
        });
      });
    } else {
      // worksheet.columns = [
      //   { header: "Sr.No.", key: "sr_no", width: 10 },
      //   { header: "Region", key: "region", width: 15 },
      //   { header: "Visit Type", key: "revisitType", width: 15 },
      //   { header: "Date", key: "created_date", width: 15 },
      //   { header: "Property Title", key: "property_name", width: 40 },
      //   { header: "Client Name", key: "customer_name", width: 20 },
      //   { header: "Mobile", key: "customer_mobile", width: 20 },
      //   // { header: "Role Title", key: "role_title", width: 20 },
      //   { header: "Email", key: "customer_email", width: 30 },
      //   { header: "Resi. Location", key: "customer_location", width: 20 },
      //   { header: "Profession", key: "customer_designation", width: 20 },
      //   { header: "Work Location", key: "customer_office_address", width: 20 },
      //   { header: "Ethnicity", key: "ethnicity", width: 20 },
      //   { header: "Visiting With", key: "visit_with", width: 20 },
      //   { header: "Visitor Status", key: "qualified", width: 20 },
      //   { header: "Lead Priority", key: "lead_priority", width: 20 },
      //   {
      //     header: "Possession Period",
      //     key: "expected_possession_period",
      //     width: 25,
      //   },
      //   { header: "Config", key: "configuration", width: 15 },
      //   { header: "Budget", key: "budget", width: 15 },
      //   { header: "Purpose", key: "purpose", width: 20 },
      //   { header: "Lead Source", key: "lead_source", width: 20 },
      //   { header: "CP Firm Name", key: "cp_firm_name", width: 50 },
      //   { header: "CP Name", key: "cp_name", width: 50 },
      //   { header: "CP Contact", key: "cp_contact", width: 20 },
      //   { header: "RERA Number", key: "rera_number", width: 20 },
      //   { header: "Referrer Name", key: "referrer_name", width: 30 },
      //   { header: "Referrer Contact", key: "referrer_contact", width: 20 },
      //   { header: "Sourcing Manager", key: "sourcing_manager", width: 30 },
      //   { header: "Closing Manager", key: "closing_manager", width: 30 },
      //   { header: "Lead Status", key: "lead_status", width: 20 },
      //   {
      //     header: "Walkin - Remark",
      //     key: "first_appointment_remarks",
      //     width: 60,
      //   },
      //   { header: "1st Follow-Up Date", key: "first_followup_date", width: 30 },
      //   { header: "Follow-up Remark", key: "last_followup_remark", width: 60 },
      //   // { header: "NFD", key: "second_followup_date", width: 20 },
      //   // { header: 'STATUS', key: 'followup_status', width: 20 },
      //   { header: "Lost Reason", key: "lost_reason", width: 60 },
      //   { header: "Lost Remark", key: "lost_remark", width: 60 },
      //   { header: "Revisit Date", key: "revisit_appointment", width: 20 },
      // ];
      worksheet.columns = columnsForExcel;
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = {
          bold: true,
          size: 14,
          color: { argb: "FFFFFF" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4F81BD" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
      });

      sortedReportData.forEach((item, index) => {
        const createdDate = item.createddate
          ? formatDate(item.createddate)
          : "N/A";
        worksheet.addRow({
          sr_no: index + 1,
          revisitType: item.revisitType,
          region: item.region || "N/A",
          created_date: createdDate,
          property_name: item.property_name || "N/A",
          customer_name: item.customer_name || "N/A",
          customer_mobile: displayPhone(item.customer_mobile),
          // role_title: item.role_title || "N/A",
          customer_email: item.customer_email || "N/A",
          customer_location: item.customer_location || "N/A",
          customer_designation: item.customer_designation || "N/A",
          customer_office_address: item.customer_office_address || "N/A",
          ethnicity: item.ethnicity || "N/A",
          visit_with: capitalizeWords(item.visit_with) || "N/A",
          qualified:
            item.qualified === true
              ? "Qualified"
              : item.qualified === false
                ? "Unqualified"
                : "N/A",
          lead_priority: capitalizeWords(item.lead_priority) || "N/A",
          expected_possession_period: item.expected_possession_period
            ? item.expected_possession_period
            : item.expected_possession_date
              ? formatDate(item.expected_possession_date)
              : "N/A",
          configuration: item.configuration || "N/A",
          budget: item.max_budget || "N/A",
          purpose: item.purpose || "N/A",
          lead_source:
            item.referrel_partner === 1
              ? "Referral Partner"
              : item.lead_source_title || "N/A",
          cp_firm_name: item.cp_firm_name || "N/A",
          cp_name: item.cp_name || "N/A",
          cp_contact: item.cp_contact || "N/A",
          rera_number: item.rera_certificate_no || "N/A",
          referrer_name: item.referrer_name || "N/A",
          referrer_contact: item.referrer_contact || "N/A",
          sourcing_manager: item.sourcing_manager || "N/A",
          closing_manager: item.closing_manager || "N/A",
          lead_status: resolveLatestLeadStatusLabel(item) || "N/A",
          cancellation_date: formatIstDateTime(item.cancellation_date),
          booking_date: formatIstDateTime(item.booking_date),
          registration_date: formatIstDateTime(item.registration_date),
          first_appointment_remarks: item.first_appointment_remarks || "N/A",
          followup_dates: SHOW_FOLLOWUP_DATE_VALUES
            ? getFollowupDatesText(item)
            : "N/A",
          last_followup_remark: item.last_followup_remark || "N/A",
          // second_followup_date: secondFollowupDate,
          // followup_status: item.followup_status || 'N/A',
          lost_reason: item.lost_reason_status_6 || "N/A",
          lost_remark: item.remarks_status_6 || "N/A",
          revisit_dates: SHOW_REVISIT_DATE_VALUES
            ? getRevisitDatesText(item)
            : "N/A",
        });
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "disposition_report.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="page-body justo-report-page justo-disposition-page">
      {isAuth && (
        <Breadcrumb
          title={strings.depositionReport}
          parentListing={null}
          titleactive={strings.depositionReport}
        />
      )}
      <div className="container-fluid justo-disposition">
          <div className="justo-report justo-disposition__card">
            <div className="justo-report__header">
              <div>
                <h2 className="justo-report__title">Disposition Report</h2>
                <p className="justo-report__subtitle">
                  Review visitor disposition, ownership, and follow-up activity.
                </p>
              </div>
              {reportData && reportData.length > 0 && (
                <button
                  className="justo-report__button justo-report__button--primary"
                  type="button"
                  onClick={exportToExcel}
                >
                  <i className="fa fa-file-excel-o" aria-hidden="true" />
                  Export
                </button>
              )}
            </div>
            <div className="justo-report__filters">
              <form className="theme-form justo-disposition__filter-form">
                <div className="row justo-disposition__filter-grid">
                  <div className="col-md-4">
                    <span className="cardText">{strings.startDate}</span>
                    <div
                      className="input-group date"
                      id="dt-date"
                      data-target-input="nearest"
                    >
                      <ClearableField
                        value={form.start_date}
                        onClear={() => clearFilterField("start_date")}
                      >
                        <input
                          className="form-control datetimepicker-input digits"
                          type="date"
                          data-target="#dt-date"
                          name="start_date"
                          value={form.start_date}
                          onChange={handleInputField}
                        />
                      </ClearableField>
                    </div>
                    {formVaild.start_date && (
                      <span style={{ color: "red" }}>
                        {formVaild.start_date}
                      </span>
                    )}
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">{strings.endDate}</span>
                    <div
                      className="input-group date"
                      id="dt-date"
                      data-target-input="nearest"
                    >
                      <ClearableField
                        value={form.end_date}
                        onClear={() => clearFilterField("end_date")}
                        disabled={endDateDisable}
                      >
                        <input
                          className="form-control datetimepicker-input digits"
                          type="date"
                          name="end_date"
                          value={form.end_date}
                          onChange={handleInputField}
                          min={form.start_date}
                          disabled={endDateDisable}
                        />
                      </ClearableField>
                    </div>
                    {formVaild.end_date && (
                      <span style={{ color: "red" }}>{formVaild.end_date}</span>
                    )}
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">{strings.propertyName}</span>

                    <Select
                      isDisabled={propertyList.length === 0}
                      className="justo-disposition__select"
                      classNamePrefix="justo-disposition-select"
                      isSearchable
                      isClearable
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      options={propertyList.map((property) => ({
                        value: property.property_title,
                        label: property.property_title,
                      }))}
                      value={
                        form.property_title
                          ? {
                              label: form.property_title,
                              value: form.property_title,
                            }
                          : null
                      }
                      onChange={handleSelectChange}
                      placeholder="Select property"
                    />
                  </div>
                </div>

                <div className="row justo-disposition__filter-grid">
                  <div className="col-md-4">
                    <span className="cardText">{strings.customerName}</span>
                    <ClearableField
                      value={form.client_name}
                      onClear={() => clearFilterField("client_name")}
                    >
                      <input
                        className="form-control digits"
                        type="text"
                        autoComplete="off"
                        name="client_name"
                        placeholder={strings.searchVisitorName}
                        value={form.client_name}
                        onChange={handleInputField}
                      />
                    </ClearableField>
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">
                      {strings.searchByMobileNumber}
                    </span>
                    <ClearableField
                      value={form.phone_number}
                      onClear={() => clearFilterField("phone_number")}
                    >
                      <input
                        className="form-control digits"
                        type="text"
                        autoComplete="off"
                        name="phone_number"
                        placeholder={strings.searchMobileNumber}
                        value={form.phone_number}
                        onChange={handleInputField}
                        maxLength="10"
                      />
                    </ClearableField>
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">{strings.leadSource}</span>
                    <Select
                      className="justo-disposition__select"
                      classNamePrefix="justo-disposition-select"
                      isSearchable
                      isClearable
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      options={leadSourceOptions}
                      value={
                        form.lead_source
                          ? {
                              label: form.lead_source,
                              value: form.lead_source,
                            }
                          : null
                      }
                      onChange={handleLeadSourceSelectChange}
                      placeholder={strings.leadSource}
                    />
                  </div>
                </div>
                {(form.client_name?.trim() || form.phone_number?.trim()) && (
                  <p
                    className="justo-disposition__visitor-search-hint"
                    style={{
                      margin: "0 0 12px",
                      fontSize: "0.8rem",
                      fontWeight: 400,
                      color: "#64748b",
                    }}
                  >
                    {strings.depositionVisitorSearchHint}
                  </p>
                )}

                <div className="row justo-disposition__filter-grid">
                  <div className="col-md-4">
                    <span className="cardText">{strings.sourcingManager}</span>
                    <ClearableField
                      value={form.sourcing_manager}
                      onClear={() => clearFilterField("sourcing_manager")}
                    >
                      <input
                        className="form-control digits"
                        type="text"
                        autoComplete="off"
                        name="sourcing_manager"
                        placeholder={strings.sourcingManager}
                        value={form.sourcing_manager}
                        onChange={handleInputField}
                      />
                    </ClearableField>
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">{strings.closingManager}</span>
                    <ClearableField
                      value={form.closing_manager}
                      onClear={() => clearFilterField("closing_manager")}
                    >
                      <input
                        className="form-control digits"
                        type="text"
                        autoComplete="off"
                        name="closing_manager"
                        placeholder={strings.closingManager}
                        value={form.closing_manager}
                        onChange={handleInputField}
                      />
                    </ClearableField>
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">
                      {strings.visitType || "Visit Type"}
                    </span>
                    <Select
                      className="justo-disposition__select"
                      classNamePrefix="justo-disposition-select"
                      isSearchable={false}
                      isClearable
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      options={visitTypeOptions}
                      value={
                        form.visit_type
                          ? {
                              label: form.visit_type,
                              value: form.visit_type,
                            }
                          : null
                      }
                      onChange={handleVisitTypeSelectChange}
                      placeholder={strings.selectVisitType || "Select Visit Type"}
                    />
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">
                      {strings.latestLeadStatus}
                    </span>
                    <Select
                      className="justo-disposition__select"
                      classNamePrefix="justo-disposition-select"
                      isSearchable
                      isClearable
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      options={LATEST_LEAD_STATUS_FILTER_OPTIONS}
                      value={
                        form.latest_lead_status
                          ? LATEST_LEAD_STATUS_FILTER_OPTIONS.find(
                              (option) =>
                                option.value === form.latest_lead_status,
                            ) || null
                          : null
                      }
                      onChange={handleLatestLeadStatusSelectChange}
                      placeholder={strings.selectLatestLeadStatus}
                    />
                  </div>
                </div>

                <div className="justo-report__filter-footer">
                  <div>
                    <div className="justo-report__filter-actions">
                      <button
                        className="justo-report__button justo-report__button--primary"
                        type="button"
                        onClick={filterDetail}
                        disabled={loading}
                        aria-busy={loading}
                      >
                        {loading ? (
                          <span
                            className="justo-report__spinner"
                            aria-hidden="true"
                          />
                        ) : (
                          <i className="fa fa-search" aria-hidden="true" />
                        )}
                        {loading ? "Loading…" : strings.submit}
                      </button>
                      <button
                        className="justo-report__button justo-report__button--secondary"
                        type="button"
                        onClick={filterReset}
                        disabled={loading}
                      >
                        <i className="fa fa-undo" aria-hidden="true" />
                        {strings.reset}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

          {reportData && reportData.length > 0 && (
            <div className="justo-report__toolbar">
              <div className="justo-report__toolbar-control">
                <label
                  htmlFor="deposition-sort-order"
                >
                  {strings.sortOrder}
                </label>
                <select
                  id="deposition-sort-order"
                  className="justo-report__compact-select"
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                    setCurrentPage(0);
                  }}
                >
                  <option value="latest">{strings.sortLatestFirst}</option>
                  <option value="oldest">{strings.sortOldestFirst}</option>
                </select>
              </div>
            </div>
          )}

          <div className="justo-report__data">
            <div>
                {apiError ? (
                  <div className="justo-report__state justo-report__state--error">
                    <div className="justo-report__state-icon">
                      <i className="fa fa-exclamation-circle"></i>
                    </div>
                    <h5>Error Loading Data</h5>
                    <p>{errorMessage}</p>
                    {/* <button 
                      className="btn btn-primary" 
                      onClick={() => {
                        setApiError(false);
                        setLoading(true);
                        dispatch(getDepostionReport({ ...form }));
                      }}
                    >
                      Try Again
                    </button> */}
                  </div>
                ) : loading ? (
                  <div
                    className="justo-report__loading"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <div className="justo-report__loading-strip">
                      <p className="justo-report__loading-hint">
                        <span
                          className="justo-report__spinner justo-report__spinner--brand"
                          aria-hidden="true"
                        />
                        Fetching disposition report…
                      </p>
                      <div
                        className="justo-report__loading-track"
                        aria-hidden="true"
                      >
                        <span className="justo-report__loading-bar" />
                      </div>
                    </div>
                    <TableListSkeleton
                      rows={10}
                      columns={12}
                      className="table-list-skeleton--wide table-list-skeleton--fill"
                      style={{ maxHeight: "500px" }}
                    />
                  </div>
                ) : reportData && reportData.length === 0 ? (
                  <div className="justo-report__state">
                    <div className="justo-report__state-icon">
                      <i className="fa fa-inbox" aria-hidden="true" />
                    </div>
                    <h5>No data available</h5>
                    <p>Try adjusting the filters to find matching records.</p>
                  </div>
                ) : (
                  <div className="justo-report__table-shell">
                    <div
                      ref={tableRegionRef}
                      className="justo-report__table-region"
                    >
                      <table
                        className="justo-report__table justo-disposition__table"
                      >
                      <thead>
                        <tr>
                          <th
                            className="justo-report__sticky-col justo-report__sticky-col--index"
                          >
                            Sr.No.
                          </th>
                          <th style={{ minWidth: "100px" }}>Region</th>
                          <th style={{ minWidth: "150px" }}>Visit Type</th>
                          <th style={{ minWidth: "100px" }}>Date</th>
                          <th
                            className="justo-report__sticky-col justo-report__sticky-col--name"
                          >
                            Client Name
                          </th>
                          <th className="justo-report__sticky-col justo-report__sticky-col--property">
                            Property Title
                          </th>
                          <th className="justo-report__sticky-col justo-report__sticky-col--mobile">
                            Mobile
                          </th>
                          {/* <th style={{ minWidth: "150px" }}>Role Title</th> */}
                          <th style={{ minWidth: "200px" }}>Email</th>
                          <th style={{ minWidth: "200px" }}>Resi. Location</th>
                          <th style={{ minWidth: "200px" }}>Profession</th>
                          <th style={{ minWidth: "200px" }}>Work Location</th>
                          <th style={{ minWidth: "200px" }}>Ethnicity</th>
                          <th style={{ minWidth: "200px" }}>Visiting With</th>
                          <th style={{ minWidth: "200px" }}>Visitor Status</th>
                          <th style={{ minWidth: "200px" }}>Lead Priority</th>
                          <th style={{ minWidth: "200px" }}>
                            Possession Period
                          </th>
                          <th style={{ minWidth: "200px" }}>Config</th>
                          <th style={{ minWidth: "200px" }}>Budget</th>
                          <th style={{ minWidth: "200px" }}>Purpose</th>
                          <th style={{ minWidth: "200px" }}>Lead Source</th>
                          <th style={{ minWidth: "200px" }}>CP Firm Name</th>
                          <th style={{ minWidth: "200px" }}>CP Name</th>
                          <th style={{ minWidth: "200px" }}>CP Contact</th>
                          <th style={{ minWidth: "200px" }}>RERA Number</th>
                          <th style={{ minWidth: "200px" }}>Referrer Name</th>
                          <th style={{ minWidth: "200px" }}>
                            Referrer Contact
                          </th>
                          <th style={{ minWidth: "200px" }}>
                            Sourcing Manager
                          </th>
                          <th style={{ minWidth: "200px" }}>Closing Manager</th>
                          <th style={{ minWidth: "200px" }}>
                            Latest / Last Updated Lead Status
                          </th>
                          <th style={{ minWidth: "160px" }}>
                            Cancellation Date
                          </th>
                          <th style={{ minWidth: "160px" }}>Booking Date</th>
                          <th style={{ minWidth: "160px" }}>
                            Registration Date
                          </th>
                          <th
                            className="justo-disposition__history-col"
                            style={{ minWidth: "110px" }}
                          >
                            Lead History
                          </th>
                          <th style={{ minWidth: "300px" }}>Walkin - Remark</th>
                          <th style={{ minWidth: "200px" }}>Followup Date</th>
                          <th style={{ minWidth: "300px" }}>
                            Follow-up / Revisit Remark
                          </th>
                          {/* <th style={{ minWidth: "200px" }}>NFD</th> */}
                          <th style={{ minWidth: "300px" }}>Lost Reason</th>
                          <th style={{ minWidth: "300px" }}>Lost Remark</th>
                          <th style={{ minWidth: "200px" }}>Revisit Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedTableRows.map((item, index) => {
                          const rowIndex = currentPage * itemsPerPage + index;
                          const statusHistory = getLeadHistoryRemarkItems(item);
                          const leadHistoryCount =
                            countDispositionRemarkEntries(item) ||
                            statusHistory.length;
                          return (
                            <tr
                              key={`${item._id || "row"}-${item.createddate || item.created_date || index}`}
                            >
                              <td className="justo-report__sticky-col justo-report__sticky-col--index">
                                {rowIndex + 1}
                              </td>
                              <td>{item.region || "N/A"}</td>
                              <td>{renderVisitTypeBadge(item.revisitType)}</td>
                              <td>
                                {item.createddate
                                  ? formatDate(item.createddate)
                                  : "N/A"}
                              </td>
                              <td className="justo-report__sticky-col justo-report__sticky-col--name">
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_name",
                                  item.customer_name,
                                )}
                              </td>
                              <td className="justo-report__sticky-col justo-report__sticky-col--property">
                                {renderExpandableCell(
                                  rowIndex,
                                  "property_name",
                                  item.property_name,
                                )}
                              </td>
                              <td className="justo-report__sticky-col justo-report__sticky-col--mobile">
                                {displayPhone(item.customer_mobile)}
                              </td>
                              {/* <td>{item.role_title || "N/A"}</td> */}
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_email",
                                  item.customer_email,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_location",
                                  capitalizeWords(item.customer_location) ||
                                    "N/A",
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_designation",
                                  item.customer_designation,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_office_address",
                                  capitalizeWords(
                                    item.customer_office_address,
                                  ) || "N/A",
                                )}
                              </td>
                              <td>{item.ethnicity || "N/A"}</td>
                              <td>
                                {capitalizeWords(item.visit_with) || "N/A"}
                              </td>
                              <td>
                                {renderVisitorQualificationBadge(item.qualified)}
                              </td>
                              <td>
                                {capitalizeWords(item.lead_priority) || "N/A"}
                              </td>
                              <td>
                                {item.expected_possession_period
                                  ? item.expected_possession_period
                                  : item.expected_possession_date
                                    ? formatDate(item.expected_possession_date)
                                    : "N/A"}
                              </td>
                              <td>{item.configuration || "N/A"}</td>
                              <td>{item.max_budget}</td>
                              <td>{item.purpose || "N/A"}</td>
                              <td>
                                {item.referrel_partner === 1
                                  ? "Referral Partner"
                                  : item.lead_source_title || "N/A"}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "cp_firm_name",
                                  item.cp_firm_name,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "cp_name",
                                  item.cp_name,
                                )}
                              </td>
                              <td>{item.cp_contact || "N/A"}</td>
                              <td>{item.rera_certificate_no || "N/A"}</td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "referrer_name",
                                  capitalizeWords(item.referrer_name) || "N/A",
                                )}
                              </td>
                              <td>{item.referrer_contact || "N/A"}</td>
                              <td>{item.sourcing_manager || "N/A"}</td>
                              <td>{item.closing_manager || "N/A"}</td>
                              <td>
                                {renderLeadStatusBadge(
                                  resolveLatestLeadStatusLabel(item),
                                )}
                              </td>
                              <td>
                                {formatIstDateTime(item.cancellation_date)}
                              </td>
                              <td>
                                {formatIstDateTime(item.booking_date)}
                              </td>
                              <td>
                                {formatIstDateTime(item.registration_date)}
                              </td>
                              <td className="justo-disposition__history-col">
                                {leadHistoryCount > 0 ? (
                                  <button
                                    type="button"
                                    className="justo-disposition__history-btn"
                                    title={`View lead history (${leadHistoryCount})`}
                                    aria-label={`View lead history (${leadHistoryCount})`}
                                    onClick={(event) => {
                                      event.currentTarget.blur();
                                      setStatusHistoryModal({
                                        name: item.customer_name || "Visitor",
                                        property: item.property_name || "",
                                        history: statusHistory,
                                      });
                                    }}
                                  >
                                    <i
                                      className="fa fa-history"
                                      aria-hidden="true"
                                    />
                                    <span className="justo-disposition__history-badge">
                                      {leadHistoryCount}
                                    </span>
                                  </button>
                                ) : (
                                  <span className="justo-disposition__history-empty">
                                    -
                                  </span>
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "first_appointment_remarks",
                                  item.first_appointment_remarks,
                                )}
                              </td>
                              <td>
                                {SHOW_FOLLOWUP_DATE_VALUES
                                  ? renderExpandableCell(
                                      rowIndex,
                                      "followup_dates",
                                      getFollowupDatesText(item),
                                    )
                                  : "N/A"}
                              </td>
                              <td
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "last_followup_remark",
                                  item.last_followup_remark,
                                )}
                              </td>
                              {/* <td>
                              {item.second_followup_date
                                ? formatDate(item.second_followup_date)
                                : "N/A"}
                            </td> */}
                              <td
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "lost_reason_status_6",
                                  item.lost_reason_status_6,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "remarks_status_6",
                                  item.remarks_status_6,
                                )}
                              </td>
                              <td>
                                {SHOW_REVISIT_DATE_VALUES
                                  ? renderExpandableCell(
                                      rowIndex,
                                      "revisit_dates",
                                      getRevisitDatesText(item),
                                    )
                                  : "N/A"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>

                    {totalRecords > 0 && (
                      <div className="justo-report__footer">
                        <div className="justo-report__page-size">
                          <label htmlFor="deposition-page-size">
                            Rows per page
                          </label>
                          <select
                            id="deposition-page-size"
                            className="justo-report__compact-select"
                            value={itemsPerPage}
                            onChange={handlePageSizeChange}
                          >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="justo-report__pagination">
                          <Pagination
                            itemsPerPage={itemsPerPage}
                            totalCount={totalRecords}
                            listData={sortedReportData || []}
                            onPageChange={handlePageChange}
                            itemOffset={currentPage}
                            setItemOffset={setCurrentPage}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
          </div>
      </div>

      {/* See more / See less overlay - keeps row height fixed */}
      {expandedCell && (
        <div
          className="property-banner-upload-modal deposition-expand-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="depositionExpandModalTitle"
          onClick={() => setExpandedCell(null)}
        >
          <div
            className="property-banner-upload-modal__dialog deposition-expand-modal__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="property-banner-upload-modal__header">
              <h5
                className="property-banner-upload-modal__title"
                id="depositionExpandModalTitle"
              >
                Remark details
              </h5>
              <button
                type="button"
                className="property-banner-upload-modal__close"
                aria-label="Close"
                onClick={() => setExpandedCell(null)}
              >
                ×
              </button>
            </div>
            <div className="property-banner-upload-modal__body deposition-expand-modal__body">
              <p className="deposition-expand-modal__label">
                {expandedCell.colKey === "last_followup_remark" &&
                parseRemarkChain(expandedCell.text).length > 1
                  ? "Remark history"
                  : "Full content"}
              </p>
              <div
                className={`deposition-expand-modal__content${
                  expandedCell.colKey === "last_followup_remark" &&
                  parseRemarkChain(expandedCell.text).length > 1
                    ? " deposition-expand-modal__content--chain"
                    : ""
                }`}
              >
                {expandedCell.colKey === "last_followup_remark"
                  ? renderRemarkChainTimeline(expandedCell.text)
                  : expandedCell.text}
              </div>
            </div>
            <div className="property-banner-upload-modal__footer">
              <button
                type="button"
                className="property-banner-upload-modal__btn property-banner-upload-modal__btn--confirm"
                onClick={() => setExpandedCell(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {statusHistoryModal && (
        <>
          <button
            type="button"
            className="justo-disposition-history__backdrop"
            aria-label="Close status history"
            onClick={() => setStatusHistoryModal(null)}
          />
          <div
            className="justo-disposition-history"
            role="dialog"
            aria-modal="true"
            aria-labelledby="depositionStatusHistoryTitle"
          >
            <div className="justo-disposition-history__header">
              <div className="justo-disposition-history__header-main">
                <p className="justo-disposition-history__eyebrow">
                  Disposition report
                </p>
                <h2
                  className="justo-disposition-history__title"
                  id="depositionStatusHistoryTitle"
                >
                  Status history
                </h2>
                <p className="justo-disposition-history__context">
                  {statusHistoryModal.name ? (
                    <span className="justo-disposition-history__context-item justo-disposition-history__context-item--strong">
                      {capitalizeWords(statusHistoryModal.name)}
                    </span>
                  ) : null}
                  {statusHistoryModal.name && statusHistoryModal.property ? (
                    <span
                      className="justo-disposition-history__context-sep"
                      aria-hidden="true"
                    >
                      ·
                    </span>
                  ) : null}
                  {statusHistoryModal.property ? (
                    <span className="justo-disposition-history__context-item">
                      {capitalizeWords(statusHistoryModal.property)}
                    </span>
                  ) : null}
                </p>
              </div>
              <button
                type="button"
                className="justo-disposition-history__close"
                aria-label="Close"
                onClick={() => setStatusHistoryModal(null)}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
            <div className="justo-disposition-history__body">
              <div className="justo-disposition-history__cols" aria-hidden="true">
                <span>Status</span>
                <span>Remark</span>
              </div>
              <ol className="justo-disposition-history__list">
                {(statusHistoryModal.history || []).map((entry, index) => {
                  const isLatest = index === 0;
                  return (
                    <li
                      key={`status-history-${index}-${entry.date || entry.label}`}
                      className={`justo-disposition-history__row${
                        isLatest ? " justo-disposition-history__row--latest" : ""
                      }`}
                    >
                      <div className="justo-disposition-history__status">
                        <div className="justo-disposition-history__status-top">
                          {isLatest ? (
                            <span className="justo-disposition-history__current-tag">
                              Latest
                            </span>
                          ) : null}
                          <p className="justo-disposition-history__date">
                            {formatDateTime(entry.date)}
                          </p>
                        </div>
                        {renderLeadStatusBadge(entry.label)}
                      </div>
                      <div className="justo-disposition-history__remark">
                        {renderHistoryRemark(entry)}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default DepositionReport;
